import type { AdminSection } from "../page";

const navGroups = [
  {
    label: "Homepage",
    items: [
      { section: "hero" as AdminSection, label: "Hero Section", icon: "ri-image-2-line", desc: "Headline, subtext, CTAs" },
      { section: "stats" as AdminSection, label: "Key Stats", icon: "ri-bar-chart-2-line", desc: "Return, term, security" },
      { section: "products" as AdminSection, label: "Investment Products", icon: "ri-funds-line", desc: "Product cards & details" },
      { section: "testimonials" as AdminSection, label: "Testimonials", icon: "ri-chat-quote-line", desc: "Investor reviews" },
    ],
  },
  {
    label: "Pages",
    items: [
      { section: "social-housing" as AdminSection, label: "Social Housing Page", icon: "ri-building-2-line", desc: "Product page content" },
      { section: "properties" as AdminSection, label: "Properties", icon: "ri-home-4-line", desc: "Asset portfolio listings" },
      { section: "team" as AdminSection, label: "Team Members", icon: "ri-team-line", desc: "About page — leadership" },
      { section: "brochure" as AdminSection, label: "Brochure", icon: "ri-file-download-line", desc: "Investment brochure" },
    ],
  },
  {
    label: "Site Settings",
    items: [
      { section: "navbar" as AdminSection, label: "Navigation", icon: "ri-navigation-line", desc: "Menu links & CTA" },
      { section: "footer" as AdminSection, label: "Footer", icon: "ri-layout-bottom-line", desc: "Footer links & info" },
      { section: "contact" as AdminSection, label: "Contact Info", icon: "ri-contacts-line", desc: "Office, phone, email" },
    ],
  },
];

interface Props {
  activeSection: AdminSection;
  onSelect: (s: AdminSection) => void;
  isOpen: boolean;
  onClose: () => void;
}

export default function AdminSidebar({ activeSection, onSelect, isOpen, onClose }: Props) {
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed lg:static top-0 left-0 bottom-0 z-50 lg:z-auto
          w-64 bg-white border-r border-slate-100 flex flex-col
          transition-transform duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {/* Sidebar header (mobile only) */}
        <div className="lg:hidden flex items-center justify-between px-4 h-14 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#071A2E] border border-[#C9A84C]/40">
              <i className="ri-home-4-fill text-[#C9A84C] text-sm leading-none" />
            </div>
            <span className="text-sm font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Admin Panel
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 cursor-pointer"
          >
            <i className="ri-close-line text-lg leading-none" />
          </button>
        </div>

        {/* Label */}
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex-shrink-0">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Content Manager</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-1">
              <p className="px-4 pt-3 pb-1 text-[9px] font-bold text-slate-300 uppercase tracking-[0.2em]">
                {group.label}
              </p>
              {group.items.map((item) => {
                const active = activeSection === item.section;
                return (
                  <button
                    key={item.section}
                    onClick={() => onSelect(item.section)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all cursor-pointer group ${
                      active
                        ? "bg-[#071A2E]/5 border-r-2 border-[#C9A84C]"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <div
                      className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${
                        active
                          ? "bg-[#C9A84C]/15 text-[#C9A84C]"
                          : "bg-slate-100 text-slate-400 group-hover:bg-[#C9A84C]/10 group-hover:text-[#C9A84C]"
                      }`}
                    >
                      <i className={`${item.icon} text-xs leading-none`} />
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`text-xs font-semibold leading-none mb-0.5 truncate ${
                          active ? "text-[#071A2E]" : "text-slate-600"
                        }`}
                      >
                        {item.label}
                      </p>
                      <p className="text-[10px] text-slate-400 leading-snug truncate">{item.desc}</p>
                    </div>
                    {active && (
                      <div className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0 ml-auto" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer info */}
        <div className="px-4 py-4 border-t border-slate-100 flex-shrink-0">
          <div className="bg-gradient-to-br from-[#071A2E]/5 to-[#C9A84C]/5 border border-[#C9A84C]/15 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-4 h-4 flex items-center justify-center text-[#C9A84C]">
                <i className="ri-information-line text-xs leading-none" />
              </div>
              <p className="text-[10px] font-bold text-[#071A2E]">Admin Panel v1.0</p>
            </div>
            <p className="text-[10px] text-slate-400 leading-relaxed">
              Review all content sections. Contact your developer to apply changes to the live site.
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}
